// Authentication state management with Zustand
import { create } from 'zustand'
import { authService } from '../services/authService'

interface User {
  id: string
  email: string
  displayName: string | null
}

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  tokenExpiresAt: number | null
  
  // Actions
  login: (idToken: string | undefined, code?: string, user?: any, provider?: 'apple' | 'google') => Promise<void>
  logout: () => void
  checkAuth: () => Promise<void>
  isTokenExpired: () => boolean
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  tokenExpiresAt: null,

  login: async (idToken: string | undefined, code?: string, user?: any, provider: 'apple' | 'google' = 'apple') => {
    set({ isLoading: true, error: null })
    
    try {
      let response
      
      if (provider === 'google' && code) {
        response = await authService.loginWithGoogle(code)
      } else {
        response = await authService.loginWithApple(idToken, code, user)
      }
      
      // Store token
      localStorage.setItem('as_token', response.token)
      
      // Calculate token expiration (7 days from now)
      const expiresAt = Date.now() + (7 * 24 * 60 * 60 * 1000)
      localStorage.setItem('as_token_expires_at', expiresAt.toString())
      
      set({
        user: response.account,
        token: response.token,
        isAuthenticated: true,
        isLoading: false,
        tokenExpiresAt: expiresAt,
      })
    } catch (error: any) {
      set({
        error: error.message || 'Login failed',
        isLoading: false,
      })
      throw error
    }
  },

  logout: () => {
    localStorage.removeItem('as_token')
    localStorage.removeItem('as_token_expires_at')
    set({
      user: null,
      token: null,
      isAuthenticated: false,
      error: null,
      tokenExpiresAt: null,
    })
  },

  checkAuth: async () => {
    const token = localStorage.getItem('as_token')
    const expiresAtStr = localStorage.getItem('as_token_expires_at')
    
    if (!token) {
      set({ isAuthenticated: false })
      return
    }
    
    // Check if token is expired based on stored expiration
    const expiresAt = expiresAtStr ? parseInt(expiresAtStr, 10) : null
    if (expiresAt && Date.now() > expiresAt) {
      console.warn('⚠️ Token expired based on stored expiration time')
      localStorage.removeItem('as_token')
      localStorage.removeItem('as_token_expires_at')
      set({
        user: null,
        token: null,
        isAuthenticated: false,
        tokenExpiresAt: null,
      })
      return
    }
    
    try {
      const user = await authService.getCurrentUser(token)
      
      set({
        user,
        token,
        isAuthenticated: true,
        tokenExpiresAt: expiresAt,
      })
    } catch (error) {
      // Token invalid or expired
      localStorage.removeItem('as_token')
      localStorage.removeItem('as_token_expires_at')
      set({
        user: null,
        token: null,
        isAuthenticated: false,
        tokenExpiresAt: null,
      })
    }
  },

  isTokenExpired: () => {
    const expiresAt = get().tokenExpiresAt
    if (!expiresAt) return true
    return Date.now() > expiresAt
  },
}))
